import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link,
  Font,
  pdf,
} from "@react-pdf/renderer";
import { getResourceFile } from "../../../redux/thunks/Resource";
import {
  ReportJobFitColors,
  ReportLabels,
  ReportLevelColors,
} from "../../../utils/Constants";
import {
  formatReportDate,
  formatReportScore,
  getAssessmentCompletedDate,
  getCandidateName,
  getNameInitials,
  getReportSections,
} from "../../../utils/utilities";
import UrbanistFont from "../../../assets/font/Urbanist/Urbanist-VariableFont_wght.ttf";
import JobFitCardShadow from "../../../assets/images/dashboard/pdf-report/job-fit-card-shadow.png";
import { CheckIcon, GlobeIcon, TestfuseLogo } from "./AssessmentReportPDFIcons";

// the app's font, its default weight is the regular one the design uses
Font.register({ family: "Urbanist", src: UrbanistFont });

// keep words whole, react-pdf hyphenates them by default
Font.registerHyphenationCallback((word) => [word]);

// the app's font classes (_typography.scss) in pt, 1px = 0.75pt, as react-pdf can't use css classes.
// their line height is the font's own, 1.2
const font = StyleSheet.create({
  headline2: { fontSize: 24 },
  headline3: { fontSize: 21 },
  headline5: { fontSize: 16.5 },
  subtitle2: { fontSize: 13.5 },
  body1: { fontSize: 12 },
  body2: { fontSize: 10.5 },
  body3: { fontSize: 9 },
  caption: { fontSize: 8.25 },
});

// the app's colour classes (_colors.scss)
const textColor = StyleSheet.create({
  dark100: { color: "#121216" },
  dark50: { color: "#808191" },
  primaryText: { color: "#6F2DBD" },
  primaryDarkText: { color: "#7C21FF" },
});

// $dark-30, the colour of dividers and bar tracks
const DIVIDER_COLOR = "#ECEDF1";

// layout of the figma design in pt, the same as the online report
const styles = StyleSheet.create({
  // text is body-2 dark-100 unless styled otherwise
  page: {
    // room for the footer, its strip and the design's 20px gap above it
    paddingBottom: 73.5,
    fontFamily: "Urbanist",
    ...font.body2,
    ...textColor.dark100,
  },
  divider: { width: 0.75, alignSelf: "stretch", backgroundColor: DIVIDER_COLOR },
  section: { gap: 9 },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 21,
    paddingHorizontal: 15,
    borderBottomWidth: 0.75,
    borderColor: DIVIDER_COLOR,
  },
  candidate: { flexDirection: "row", alignItems: "center", gap: 9 },
  avatar: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 18,
    backgroundColor: "#F2E9FF",
  },
  candidateName: { gap: 3 },
  company: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    maxWidth: "50%",
    minHeight: 25.5,
  },
  companyLogo: { height: 18, maxWidth: 72, objectFit: "contain" },
  companyName: { letterSpacing: 1.5, textTransform: "uppercase" },
  companyNameWithLogo: { letterSpacing: 0.75 },

  content: { gap: 30, paddingTop: 18, paddingHorizontal: 18 },

  summary: { flexDirection: "row", alignItems: "flex-start", gap: 18 },
  summaryText: { flexGrow: 0, flexShrink: 1, flexBasis: 316.5, gap: 9 },
  // react-pdf can't draw shadows, so the design's shadow is an image behind the card,
  // spread 90px (67.5pt) around it
  jobFitCardShadow: {
    position: "absolute",
    top: -67.5,
    right: -67.5,
    bottom: -67.5,
    left: -67.5,
  },
  jobFitCard: {
    width: 174,
    alignItems: "center",
    gap: 3,
    padding: 9,
    borderRadius: 6,
    backgroundColor: "#F5F5F5",
  },
  jobFitBadge: {
    marginBottom: 3,
    paddingVertical: 2.25,
    paddingHorizontal: 5.25,
    borderWidth: 0.75,
    borderRadius: 3,
  },
  // the design's size for this note, below the smallest font class
  jobFitNote: { fontSize: 7.5, textAlign: "center" },

  scoreOverview: { flexDirection: "row" },
  // a third of the row each, also when fewer bars are shown
  scoreItem: { width: 169.5, gap: 3 },
  scoreDivider: { marginHorizontal: 12 },
  scoreLabel: { flexDirection: "row", gap: 6 },
  scoreBarRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  scoreBar: {
    flex: 1,
    height: 3,
    borderRadius: 2.25,
    backgroundColor: DIVIDER_COLOR,
  },
  scoreBarFill: { height: 3, borderRadius: 2.25 },

  columns: { flexDirection: "row", gap: 15 },
  // strengths and skills are vertically centered on each other, as in the design
  strengthsRow: { alignItems: "center" },
  // column widths of the design, a single column fills the row
  strengthsColumn: { flex: 365, gap: 9 },
  skillsColumn: { flex: 341, gap: 9 },
  areasColumn: { flex: 398, gap: 9 },
  recommendationColumn: { flex: 308, gap: 9 },

  points: { gap: 9 },
  point: { gap: 3 },
  pointTitle: { flexDirection: "row", alignItems: "center", gap: 6 },
  pointDescription: { paddingLeft: 13.5 },

  tableRow: { flexDirection: "row", alignItems: "center" },
  tableBodyRow: { borderTopWidth: 0.75, borderColor: DIVIDER_COLOR },
  tableCell: { width: "33.33%", paddingVertical: 6, paddingHorizontal: 9 },
  // the header text doesn't wrap, as in the design
  tableHeaderCell: { paddingRight: 0 },
  // the line is inside the cell's top padding, as in the design
  tableBodyCell: { paddingTop: 5.25 },
  // many skills: tighter rows and a wider skill column, so the report stays one page
  compactTableCell: { paddingVertical: 2.25, paddingRight: 3 },
  compactTableBodyCell: { paddingTop: 1.5 },

  status: {
    alignSelf: "flex-start",
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 3,
  },

  footer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 9,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    paddingTop: 11.25,
    paddingBottom: 12,
    paddingHorizontal: 15,
    borderTopWidth: 0.75,
    borderColor: DIVIDER_COLOR,
  },
  footerDate: { gap: 3 },
  footerLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    textDecoration: "none",
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 9,
    backgroundColor: "#896BFF",
  },
});

const COMPACT_TABLE_COLUMN_WIDTHS = [
  { width: "56%" },
  { width: "28%" },
  { width: "16%" },
];

const getAssessmentCandidateName = (assessment) =>
  getCandidateName(assessment?.user?.first_name, assessment?.user?.last_name);

// company logo as a data url. react-pdf only renders png and jpg images,
// so other formats like svg or webp are drawn into a png
const loadCompanyLogo = async (dispatch, imageId) => {
  if (!imageId) return null;
  try {
    const result = await dispatch(getResourceFile({ id: imageId }));
    if (result.error) return null;
    const logo = result.payload;
    if (/^data:image\/(png|jpeg);/.test(logo)) return logo;

    return await new Promise((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => {
        // drawn at four times the logo's height (24px, 18pt), so it stays sharp
        const scale = Math.max(1, 96 / (image.naturalHeight || 24));
        const canvas = document.createElement("canvas");
        canvas.width = (image.naturalWidth || 96) * scale;
        canvas.height = (image.naturalHeight || 24) * scale;
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      image.onerror = reject;
      image.src = logo;
    });
  } catch (error) {
    return null;
  }
};

const JobFitCard = ({ jobFit }) => {
  const colors = ReportJobFitColors[jobFit?.label];
  return (
    <View>
      <Image src={JobFitCardShadow} style={styles.jobFitCardShadow} />
      <View style={styles.jobFitCard}>
        {colors && (
          <View
            style={[
              styles.jobFitBadge,
              { backgroundColor: colors.background, borderColor: colors.border },
            ]}
          >
            <Text style={font.caption}>{jobFit.label}</Text>
          </View>
        )}
        <Text>{ReportLabels.jobFitScore}</Text>
        {jobFit ? (
          <Text style={[font.headline2, textColor.primaryDarkText]}>
            {formatReportScore(jobFit.score)}
            <Text style={[font.body1, textColor.dark50]}>/10</Text>
          </Text>
        ) : (
          <Text style={[font.body1, textColor.dark50]}>
            {ReportLabels.jobFitUnavailable}
          </Text>
        )}
        <Text style={[styles.jobFitNote, textColor.dark50]}>
          {jobFit ? ReportLabels.jobFitNote : ReportLabels.jobFitUnavailableNote}
        </Text>
      </View>
    </View>
  );
};

const ScoreBar = ({ title, item }) => {
  const levelColor = ReportLevelColors[item.label];
  return (
    <View style={styles.scoreItem}>
      <View style={styles.scoreLabel}>
        <Text>{title}</Text>
        <Text style={{ color: levelColor }}>{item.label}</Text>
      </View>
      <View style={styles.scoreBarRow}>
        <View style={styles.scoreBar}>
          <View
            style={[
              styles.scoreBarFill,
              { width: `${item.score * 10}%`, backgroundColor: levelColor },
            ]}
          />
        </View>
        <Text style={[font.body1, textColor.dark50]}>
          <Text style={{ color: levelColor }}>
            {formatReportScore(item.score)}
          </Text>
          /10
        </Text>
      </View>
      <Text style={[font.body3, textColor.dark50]}>{item.description}</Text>
    </View>
  );
};

const PointList = ({ items, icon }) => (
  <View style={styles.points}>
    {items.map((point, index) => (
      <View style={styles.point} key={index}>
        <View style={styles.pointTitle}>
          {icon}
          <Text>{point.title}</Text>
        </View>
        {point.description ? (
          <Text style={[styles.pointDescription, font.body3, textColor.dark50]}>
            {point.description}
          </Text>
        ) : null}
      </View>
    ))}
  </View>
);

// style of a skills table cell, with the same font classes as the online report's table
const getTableCellStyle = (column, isBody, isCompact) => {
  const layout = isCompact
    ? [
      styles.tableCell,
      styles.compactTableCell,
      COMPACT_TABLE_COLUMN_WIDTHS[column],
      isBody ? styles.compactTableBodyCell : styles.tableHeaderCell,
    ]
    : [styles.tableCell, isBody ? styles.tableBodyCell : styles.tableHeaderCell];
  const text = isBody
    ? [isCompact ? font.caption : font.body3]
    : [isCompact ? font.body3 : font.body2, textColor.dark50];
  return [...layout, ...text];
};

export default function AssessmentReportPDF({ assessment, companyLogo }) {
  const report = assessment?.downloadable_report || {};
  const {
    scoreOverviewBars,
    keyStrengths,
    skillsInsights,
    areasToValidate,
    recommendation,
    statusColors,
    isCompactTable,
  } = getReportSections(report);
  const candidateName = getAssessmentCandidateName(assessment);
  const companyName = assessment?.tenant?.company_name;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.candidate}>
            <View style={styles.avatar}>
              <Text style={[font.headline5, textColor.primaryText]}>
                {getNameInitials(candidateName)}
              </Text>
            </View>
            <View style={styles.candidateName}>
              <Text style={font.headline3}>{candidateName}</Text>
              <Text style={textColor.primaryDarkText}>{assessment?.title}</Text>
            </View>
          </View>
          {(companyLogo || companyName) && (
            <View style={styles.company}>
              {companyLogo && (
                <Image src={companyLogo} style={styles.companyLogo} />
              )}
              {companyName && (
                <Text
                  style={
                    companyLogo
                      ? [styles.companyName, styles.companyNameWithLogo]
                      : [font.subtitle2, styles.companyName]
                  }
                >
                  {companyName}
                </Text>
              )}
            </View>
          )}
        </View>

        <View style={styles.content}>
          <View style={styles.summary}>
            <View style={styles.summaryText}>
              <Text style={font.subtitle2}>{ReportLabels.executiveSummary}</Text>
              <Text style={textColor.dark50}>{report.executive_summary}</Text>
            </View>
            <View style={styles.divider} />
            <JobFitCard jobFit={report.job_fit} />
          </View>

          {scoreOverviewBars.length > 0 && (
            <View style={styles.section}>
              <Text style={font.subtitle2}>{ReportLabels.scoreOverview}</Text>
              <View style={styles.scoreOverview}>
                {scoreOverviewBars.map(({ key, title }, index) => (
                  <React.Fragment key={key}>
                    {index > 0 && (
                      <View style={[styles.divider, styles.scoreDivider]} />
                    )}
                    <ScoreBar title={title} item={report.score_overview[key]} />
                  </React.Fragment>
                ))}
              </View>
            </View>
          )}

          {(keyStrengths.length > 0 || skillsInsights.length > 0) && (
            <View style={[styles.columns, styles.strengthsRow]}>
              {keyStrengths.length > 0 && (
                <View style={styles.strengthsColumn}>
                  <Text style={font.subtitle2}>{ReportLabels.keyStrengths}</Text>
                  <PointList items={keyStrengths} icon={<CheckIcon />} />
                </View>
              )}
              {keyStrengths.length > 0 && skillsInsights.length > 0 && (
                <View style={styles.divider} />
              )}
              {skillsInsights.length > 0 && (
                <View style={styles.skillsColumn}>
                  <Text style={font.subtitle2}>{ReportLabels.skillsInsights}</Text>
                  <View>
                    <View style={styles.tableRow}>
                      {ReportLabels.skillsTableHeadings.map((heading, column) => (
                        <Text
                          style={getTableCellStyle(column, false, isCompactTable)}
                          key={heading}
                        >
                          {heading}
                        </Text>
                      ))}
                    </View>
                    {skillsInsights.map((row, index) => (
                      <View style={[styles.tableRow, styles.tableBodyRow]} key={index}>
                        <Text style={getTableCellStyle(0, true, isCompactTable)}>
                          {row.skill}
                        </Text>
                        <Text style={getTableCellStyle(1, true, isCompactTable)}>
                          {row.level_assessed}
                        </Text>
                        <Text
                          style={[
                            ...getTableCellStyle(2, true, isCompactTable),
                            { color: ReportLevelColors[row.match] },
                          ]}
                        >
                          {row.match}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>
          )}

          <View style={styles.columns}>
            {areasToValidate.length > 0 && (
              <View style={styles.areasColumn}>
                <Text style={font.subtitle2}>{ReportLabels.areasToValidate}</Text>
                <PointList
                  items={areasToValidate}
                  icon={<Text style={{ color: ReportLevelColors.Good }}>→</Text>}
                />
              </View>
            )}
            {areasToValidate.length > 0 && <View style={styles.divider} />}
            <View style={styles.recommendationColumn}>
              <Text style={font.subtitle2}>
                {ReportLabels.hiringRecommendation}
              </Text>
              {statusColors && (
                <Text
                  style={[
                    styles.status,
                    {
                      backgroundColor: statusColors.background,
                      color: statusColors.text,
                    },
                  ]}
                >
                  {recommendation.status}
                </Text>
              )}
              <Text style={[font.body3, textColor.dark50]}>
                {recommendation.description}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <View style={styles.footerDate}>
            <Text style={[font.caption, textColor.dark50]}>
              {ReportLabels.assessmentDate}
            </Text>
            <Text style={font.caption}>
              {formatReportDate(getAssessmentCompletedDate(assessment))}
            </Text>
          </View>
          <Link
            src={`${window.location.origin}/pdf-report/${assessment?.id}`}
            style={styles.footerLink}
          >
            <GlobeIcon />
            <Text style={[font.body3, textColor.primaryDarkText]}>
              View Online Report →
            </Text>
          </Link>
          <TestfuseLogo />
        </View>

        <View style={styles.bottomBar} fixed />
      </Page>
    </Document>
  );
}

// saves the report pdf, in a folder the user picks where the browser supports it (chrome, edge)
export const downloadAssessmentReport = async (dispatch, assessment) => {
  const fileName = `${getAssessmentCandidateName(assessment)}-assessment-report.pdf`;

  // the save dialog opens first, browsers only allow it right after the click
  let fileHandle = null;
  if (window.showSaveFilePicker) {
    try {
      fileHandle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          { description: "PDF report", accept: { "application/pdf": [".pdf"] } },
        ],
      });
    } catch (error) {
      // closed without saving
      if (error.name === "AbortError") return;
    }
  }

  const companyLogo = await loadCompanyLogo(dispatch, assessment?.tenant?.image_id);
  const blob = await pdf(
    <AssessmentReportPDF assessment={assessment} companyLogo={companyLogo} />,
  ).toBlob();

  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  // other browsers save it to their downloads folder
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // after the browser has started the download
  setTimeout(() => URL.revokeObjectURL(url), 40000);
};
